/**
 * Media library — folder tree on the left, grid view of images on the right.
 *
 * Drag-and-drop upload (single or batch) → R2 via /admin/media. Click any
 * image to open the detail panel where you can edit alt text or delete.
 *
 * For files >100 MB, the API has a multipart-init endpoint planned;
 * here we use the simple POST flow.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, useCallback } from "react";
import { Folder, FolderPlus, Trash2, Upload, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { MAX_MEDIA_BYTES, MAX_MEDIA_MB, tooLargeMessage } from "@/lib/uploads";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/_app/media/")({ component: MediaPage });

interface MediaItem {
  id: number;
  r2_key: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  alt: string | null;
  folder_id: number | null;
  ts: number;
}

interface MediaFolder {
  id: number;
  parent_id: number | null;
  name: string;
}

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const r2Url = (k: string) => `${apiBase}/admin/media/file/${k}`;
const fmtSize = (b: number) => b < 1024 ? `${b}B` : b < 1_048_576 ? `${(b / 1024).toFixed(1)}KB` : `${(b / 1_048_576).toFixed(1)}MB`;

function MediaPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [folderId, setFolderId] = useState<number | null>(null);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data: foldersData } = useQuery({
    queryKey: ["media", "folders"],
    queryFn: () => api.get<{ items: MediaFolder[] }>("/admin/media/folders"),
  });

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["media", "list", folderId],
    queryFn: () => fetchAll<MediaItem>("/admin/media", { query: { folder: folderId ?? undefined } }),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      if (folderId) fd.append("folder_id", String(folderId));
      return api.upload<{ id: number }>("/admin/media", fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media", "list"] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/media/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "list"] });
      setSelected(null);
      toast.success("File deleted");
    },
  });

  const handleFiles = useCallback(async (files: FileList) => {
    if (!can("media", "create")) { toast.error("You don't have permission to upload."); return; }
    const arr = Array.from(files);
    let ok = 0;
    for (const f of arr) {
      // Per file, not per batch: one oversized image in a multi-select should
      // be named and skipped, not silently take the rest of the drop with it.
      if (f.size > MAX_MEDIA_BYTES) { toast.error(tooLargeMessage(f, MAX_MEDIA_MB)); continue; }
      try { await upload.mutateAsync(f); ok++; }
      catch { toast.error(`Failed: ${f.name}`); }
    }
    if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
  }, [upload, can]);

  return (
    <div className="space-y-2">
      <PageHeader
        title="Media library"
        description="Photos, posters, recruiter logos. Stored in Cloudflare R2."
        actions={
          <>
            <Button variant="outline" size="sm" disabled={!can("media", "create")} onClick={() => setNewFolderOpen(true)}>
              <FolderPlus className="h-4 w-4" /> New folder
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files) handleFiles(e.target.files); e.target.value = ""; }}
            />
            <Button size="sm" disabled={!can("media", "create")} onClick={() => fileInput.current?.click()}>
              <Upload className="h-4 w-4" /> Upload
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        {/* Folder tree */}
        <aside className="space-y-1">
          <button
            onClick={() => setFolderId(null)}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
              folderId === null ? "bg-secondary font-medium" : "hover:bg-secondary",
            )}
          >
            <Folder className="h-4 w-4" />
            Root
          </button>
          {(foldersData?.items ?? []).filter((f) => f.parent_id === null).map((f) => (
            <FolderNode
              key={f.id}
              folder={f}
              all={foldersData?.items ?? []}
              currentId={folderId}
              onSelect={setFolderId}
            />
          ))}
        </aside>

        {/* Grid */}
        <div
          className={cn(
            "min-h-[400px] rounded-lg border bg-background p-3 transition-colors",
            dragOver && "border-primary bg-primary/5",
          )}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files); }}
        >
          {isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (itemsData?.items ?? []).length === 0 ? (
            <div className="grid place-items-center py-20 text-center">
              <Upload className="mb-3 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Drag &amp; drop files here, or click Upload above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {itemsData!.items.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setSelected(m)}
                  className={cn(
                    "group relative aspect-square overflow-hidden rounded-md border bg-secondary",
                    "transition-all hover:border-primary hover:shadow-md",
                    selected?.id === m.id && "ring-2 ring-primary",
                  )}
                >
                  <img src={r2Url(m.r2_key)} alt={m.alt ?? ""} loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">
                    {fmtSize(m.size)}
                    {m.width && m.height && ` · ${m.width}×${m.height}`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <Dialog open onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Image details</DialogTitle>
              <DialogDescription>{selected.r2_key}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-[1fr_240px] gap-4">
              <img src={r2Url(selected.r2_key)} alt={selected.alt ?? ""} className="w-full rounded-md border object-contain" />
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Type</p>
                  <p>{selected.mime}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider">Size</p>
                  <p>{fmtSize(selected.size)}</p>
                </div>
                {selected.width && selected.height && (
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wider">Dimensions</p>
                    <p>{selected.width} × {selected.height}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Public URL</p>
                  <Button variant="outline" size="sm" className="w-full justify-between" onClick={() => {
                    navigator.clipboard.writeText(r2Url(selected.r2_key));
                    toast.success("Copied");
                  }}>
                    <Copy className="h-3.5 w-3.5" />
                    Copy URL
                  </Button>
                </div>
                <div>
                  <Label htmlFor="alt-text" className="text-xs uppercase tracking-wider">Alt text</Label>
                  <Input
                    id="alt-text"
                    defaultValue={selected.alt ?? ""}
                    onBlur={async (e) => {
                      const value = e.target.value;
                      if (value === (selected.alt ?? "")) return;
                      try {
                        await api.patch(`/admin/media/${selected.id}`, { alt: value });
                        toast.success("Alt updated");
                        qc.invalidateQueries({ queryKey: ["media"] });
                      } catch { toast.error("Failed to update"); }
                    }}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="destructive"
                disabled={!can("media", "delete")}
                onClick={() => { if (confirm("Delete this image? It will be removed from R2.")) remove.mutate(selected.id); }}
              >
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                <X className="h-4 w-4" /> Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* New folder dialog */}
      <NewFolderDialog open={newFolderOpen} onOpenChange={setNewFolderOpen} parentId={folderId} />
    </div>
  );
}

function FolderNode({
  folder, all, currentId, onSelect, depth = 0,
}: {
  folder: MediaFolder;
  all: MediaFolder[];
  currentId: number | null;
  onSelect: (id: number) => void;
  depth?: number;
}) {
  const children = all.filter((f) => f.parent_id === folder.id);
  return (
    <div>
      <button
        onClick={() => onSelect(folder.id)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
          currentId === folder.id ? "bg-secondary font-medium" : "hover:bg-secondary",
        )}
        style={{ paddingLeft: `${0.75 + depth}rem` }}
      >
        <Folder className="h-4 w-4" />
        {folder.name}
      </button>
      {children.length > 0 && (
        <div className="ml-2 border-l">
          {children.map((c) => (
            <FolderNode key={c.id} folder={c} all={all} currentId={currentId} onSelect={onSelect} depth={depth + 0.5} />
          ))}
        </div>
      )}
    </div>
  );
}

function NewFolderDialog({
  open, onOpenChange, parentId,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  parentId: number | null;
}) {
  const [name, setName] = useState("");
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () => api.post("/admin/media/folders", { name, parent_id: parentId }),
    onSuccess: () => {
      toast.success("Folder created");
      qc.invalidateQueries({ queryKey: ["media", "folders"] });
      setName("");
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>{parentId ? "Inside the current folder" : "At the root level"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="folder-name">Name</Label>
          <Input id="folder-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Faculty 2026" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
